import React, { useRef, useCallback, useMemo } from 'react';
import { useCanvas } from '../../../hooks/useCanvas';
import { autoScrollTimeline, autoScrollTimelineVertical } from '../../../utils/timelineUtils';
import { useTimelineTrackSprites } from '../../../hooks/useTimelineTrackSprites';

// Global flag to prevent click events after drag across all tracks
let globalJustFinishedDragging = false;
let globalDragTimeout = null;

// Snapping configuration
const SNAP_THRESHOLD = 50; // pixels - how close before snapping occurs (larger = easier to snap and stay snapped)
const SNAP_ZONES = ['start', 'end']; // Which edges to snap

const TimelineTrack = ({
  element,
  index,
  duration,
  selectedElements = [],
  onElementClick,
  globalDragState,
  setGlobalDragState,
  reorderElement,
  TRACK_HEIGHT,
  timelineWidth,
  snapEnabled = true,
}) => {
  const {
    selectElement,
    updateElementTimeline,
    updateMultipleElementTimelines,
    canvasElements
  } = useCanvas();

  // Always get fresh element data from store to avoid stale state
  const currentElement = canvasElements.find(el => el.id === element.id) || element;

  // State and Refs
  const containerRef = useRef(null);

  // Derived constants and state
  const isSelected = selectedElements.some(el => el.id === element.id);
  const isMultiSelected = selectedElements.length > 1 && isSelected;
  const FPS = 25;
  const DRAG_THRESHOLD = 5;
  const DIRECTION_LOCK_THRESHOLD = 15;

  // Single source of truth for time calculations
  const timeData = {
    startTime: (currentElement.timeline?.inPoint ?? 0) / FPS,
    duration: currentElement.timeline?.outPoint !== undefined && currentElement.timeline?.inPoint !== undefined
      ? (currentElement.timeline.outPoint - currentElement.timeline.inPoint) / FPS
      : (currentElement.componentData?.frameDuration ?? FPS * 10) / FPS,
  };

  // Determine which drag state to use
  const dragState = globalDragState;
  const setDragState = setGlobalDragState;

  // Check if this element is being dragged
  const isBeingDragged = dragState?.isMultiDrag
    ? dragState?.multiDragData?.some(d => d.id === element.id)
    : dragState?.elementId === element.id;
  const isBeingVerticallyDragged = isBeingDragged && dragState?.mode === 'vertical';

  const getCurrentTimeData = () => {
    if (isBeingDragged && dragState?.mode === 'horizontal') {
      if (dragState.isMultiDrag) {
        return dragState.multiDragData.find(d => d.id === element.id)?.currentValues || timeData;
      }
      return dragState.currentValues;
    }
    return timeData;
  };

  const currentTimeData = getCurrentTimeData();
  const leftPosition = (currentTimeData.startTime / duration) * 100;
  const width = (currentTimeData.duration / duration) * 100;

  /**
   * Find snapping candidates for the current drag operation
   * Returns the closest snap point if within threshold
   */
  const findSnapPoint = useCallback((proposedStartTime, proposedDuration, draggedElementIds, dragType) => {
    if (!containerRef.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();
    const pixelsPerSecond = containerRect.width / duration;

    // Get all other elements that aren't being dragged
    const otherElements = canvasElements.filter(el => !draggedElementIds.includes(el.id));

    let closestSnap = null;
    let minDistance = SNAP_THRESHOLD;

    const proposedStart = proposedStartTime;
    const proposedEnd = proposedStartTime + proposedDuration;

    otherElements.forEach(otherEl => {
      const otherStartFrame = otherEl.timeline?.inPoint ?? 0;
      const otherEndFrame = otherEl.timeline?.outPoint ?? (otherStartFrame + (otherEl.componentData?.frameDuration ?? FPS * 10));

      const otherStart = otherStartFrame / FPS;
      const otherEndPlusOne = (otherEndFrame + 1) / FPS;

      // Build snap combinations based on drag type
      let snapCombinations = [];

      if (dragType === 'start') {
        // When resizing start, only check start edge snaps
        snapCombinations = [
          { type: 'start-to-start', value: otherStart, offset: otherStart - proposedStart },
          { type: 'start-to-end', value: otherEndPlusOne, offset: otherEndPlusOne - proposedStart },
        ];
      } else if (dragType === 'end') {
        // When resizing end, only check end edge snaps
        snapCombinations = [
          { type: 'end-to-start', value: otherStart, offset: otherStart - proposedEnd },
          { type: 'end-to-end', value: otherEndPlusOne, offset: otherEndPlusOne - proposedEnd },
        ];
      } else {
        // When moving, check all snaps
        snapCombinations = [
          { type: 'start-to-start', value: otherStart, offset: otherStart - proposedStart },
          { type: 'start-to-end', value: otherEndPlusOne, offset: otherEndPlusOne - proposedStart },
          { type: 'end-to-start', value: otherStart, offset: otherStart - proposedEnd },
          { type: 'end-to-end', value: otherEndPlusOne, offset: otherEndPlusOne - proposedEnd },
        ];
      }

      snapCombinations.forEach(snap => {
        const distanceInPixels = Math.abs(snap.offset * pixelsPerSecond);

        if (distanceInPixels < minDistance) {
          minDistance = distanceInPixels;
          closestSnap = {
            ...snap,
            distance: distanceInPixels
          };
        }
      });
    });

    return closestSnap;
  }, [canvasElements, duration, FPS]);

  /**
   * Apply snap adjustment to the proposed time
   */
  const applySnap = useCallback((proposedStartTime, proposedDuration, draggedElementIds, dragType) => {
    // If snapping is disabled, return values unchanged
    if (!snapEnabled) {
      return { startTime: proposedStartTime, duration: proposedDuration, snapped: false };
    }

    const snap = findSnapPoint(proposedStartTime, proposedDuration, draggedElementIds, dragType);

    if (!snap) return { startTime: proposedStartTime, duration: proposedDuration, snapped: false };

    let adjustedStartTime = proposedStartTime;
    let adjustedDuration = proposedDuration;

    // Apply snap based on type
    if (snap.type === 'start-to-start') {
      // Snapping the start edge to another track's start
      if (dragType === 'move') {
        adjustedStartTime = snap.value;
      } else if (dragType === 'start') {
        adjustedStartTime = snap.value;
        adjustedDuration = (proposedStartTime + proposedDuration) - adjustedStartTime;
      }
    } else if (snap.type === 'start-to-end') {
      // Snapping the start edge to another track's end (sequencing - already has +1 frame in snap.value)
      if (dragType === 'move') {
        adjustedStartTime = snap.value;
      } else if (dragType === 'start') {
        adjustedStartTime = snap.value;
        adjustedDuration = (proposedStartTime + proposedDuration) - adjustedStartTime;
      }
    } else if (snap.type === 'end-to-start') {
      // Snapping the end edge to another track's start
      if (dragType === 'move') {
        adjustedStartTime = snap.value - proposedDuration;
      } else if (dragType === 'end') {
        adjustedDuration = snap.value - proposedStartTime;
      }
    } else if (snap.type === 'end-to-end') {
      // Snapping the end edge to another track's end (already has +1 frame in snap.value)
      if (dragType === 'move') {
        adjustedStartTime = snap.value - proposedDuration;
      } else if (dragType === 'end') {
        adjustedDuration = snap.value - proposedStartTime;
      }
    }

    return {
      startTime: adjustedStartTime,
      duration: adjustedDuration,
      snapped: true,
      snapType: snap.type
    };
  }, [findSnapPoint, FPS, snapEnabled]);

  const handleTrackClick = (e) => {
    if (e.target.closest('.drag-handle') || globalJustFinishedDragging) {
      return;
    }
    onElementClick(currentElement, e);
  };

  const startDrag = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) return;

    const isMultiMove = isMultiSelected && type === 'move';

    const commonState = {
      type,
      mode: undefined,
      lockedMode: null,
      startX: e.clientX,
      startY: e.clientY,
      elementId: element.id,
      hasMoved: false,
      originalIndex: index,
      newVisualIndex: index,
      deltaY: 0,
      initialScrollTop: 0,
    };

    // Capture initial scroll top
    if (containerRef.current) {
      const scrollContainer = containerRef.current.closest('.timeline-scroll');
      if (scrollContainer) {
        commonState.initialScrollTop = scrollContainer.scrollTop;
      }
    }

    if (isMultiMove) {
      // For multi-drag, we need to track all selected elements in visual order
      const sortedVisualElements = [...canvasElements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));

      // Build multiDragData with proper indexing
      const multiDragData = selectedElements.map(el => {
        const freshEl = canvasElements.find(e => e.id === el.id) || el;
        const elInPoint = freshEl.timeline?.inPoint || 0;
        const elOutPoint = freshEl.timeline?.outPoint || (elInPoint + (freshEl.componentData?.frameDuration || FPS * 10));
        const elTimeData = { startTime: elInPoint / FPS, duration: (elOutPoint - elInPoint) / FPS };
        const elIndex = sortedVisualElements.findIndex(e => e.id === el.id);
        return {
          id: el.id,
          originalValues: { ...elTimeData },
          currentValues: { ...elTimeData },
          originalIndex: elIndex
        };
      });

      // Find the TOPMOST (smallest index) selected element as reference
      const topMostIndex = Math.min(...multiDragData.map(d => d.originalIndex));

      // Calculate the offset for each element relative to the topmost
      const enrichedMultiDragData = multiDragData.map(item => ({
        ...item,
        offsetFromTop: item.originalIndex - topMostIndex
      }));

      setGlobalDragState({
        ...commonState,
        isMultiDrag: true,
        multiDragData: enrichedMultiDragData,
        originalIndex: topMostIndex,
        newVisualIndex: topMostIndex,
        topMostIndex: topMostIndex,  // Store explicitly for clarity
      });
    } else {
      const newState = {
        ...commonState,
        isMultiDrag: false,
        originalValues: { ...timeData },
        currentValues: { ...timeData }
      };
      setGlobalDragState(newState);
      if (!isSelected) {
        selectElement(element);
      }
    }
  }, [timeData, element, index, isMultiSelected, selectedElements, setGlobalDragState, selectElement, isSelected, canvasElements, FPS]);

  const handleDrag = useCallback((e) => {
    if (!dragState || !containerRef.current) return;

    const deltaX = e.clientX - dragState.startX;
    const scrollContainer = containerRef.current.closest('.timeline-scroll');
    const currentScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
    const scrollDelta = currentScrollTop - (dragState.initialScrollTop || 0);

    // Adjust visual deltaY to account for scrolling
    // If we scroll down (positive delta), elements move up visually, so we need to add to deltaY to keep element under mouse
    let deltaY = (e.clientY - dragState.startY) + scrollDelta;

    // Clamp deltaY to valid list bounds to prevent dragging into "blank space"
    // Calculate drag limits based on original position and total list size
    const draggedCount = dragState.isMultiDrag && dragState.multiDragData ? dragState.multiDragData.length : 1;
    const maxIndexForTop = Math.max(0, canvasElements.length - draggedCount);

    // Allow a full track height buffer so the user can drag "past" the last element to clearly see the drop zone
    const MAX_BUFFER = TRACK_HEIGHT;

    // Bounds relative to the original position
    const minDeltaY = (-dragState.originalIndex * TRACK_HEIGHT) - MAX_BUFFER;
    const maxDeltaY = ((maxIndexForTop - dragState.originalIndex) * TRACK_HEIGHT) + MAX_BUFFER;

    deltaY = Math.max(minDeltaY, Math.min(maxDeltaY, deltaY));

    // Mark as moved if threshold exceeded
    if (!dragState.hasMoved && (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD)) {
      setDragState(prev => ({ ...prev, hasMoved: true }));
    }

    // Determine and lock direction after threshold
    let currentMode = dragState.lockedMode || dragState.mode;

    if (!dragState.lockedMode && dragState.hasMoved) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      // Lock direction once movement exceeds threshold
      if (absX > DIRECTION_LOCK_THRESHOLD || absY > DIRECTION_LOCK_THRESHOLD) {
        currentMode = absY > absX ? 'vertical' : 'horizontal';
        setDragState(prev => ({ ...prev, lockedMode: currentMode, mode: currentMode }));
      } else {
        // Before locking, show tentative direction
        currentMode = absY > absX ? 'vertical' : 'horizontal';
      }
    }

    // Auto-scroll based on mode
    if (scrollContainer) {
      if (currentMode === 'horizontal') {
        autoScrollTimeline(e, scrollContainer);
      } else if (currentMode === 'vertical') {
        autoScrollTimelineVertical(e, scrollContainer);
      }
    }

    // Calculate both horizontal and vertical updates based on locked mode
    const containerRect = containerRef.current.getBoundingClientRect();
    const deltaTime = (deltaX / containerRect.width) * duration;

    // Calculate vertical displacement in terms of track heights
    const displacedBy = Math.round(deltaY / TRACK_HEIGHT);

    // Calculate the new potential index based on the original topmost element and displacement
    let newIndex = dragState.originalIndex + displacedBy;

    // Correctly clamp the index to the valid range of insertion points [0, length].
    newIndex = Math.max(0, Math.min(canvasElements.length, newIndex));

    // Only update the dimension that matches the locked mode
    let stateUpdate = { mode: currentMode };

    // Horizontal calculations (only if mode is horizontal or not yet locked)
    if (!dragState.lockedMode || currentMode === 'horizontal') {
      if (dragState.isMultiDrag) {
        const draggedIds = dragState.multiDragData.map(item => item.id);

        const newMultiDragData = dragState.multiDragData.map(item => {
          let newStartTime = Math.max(0, Math.min(item.originalValues.startTime + deltaTime, duration - item.originalValues.duration));
          let newDuration = item.originalValues.duration;
          let isSnapped = false;

          if (dragState.type === 'start' || dragState.type === 'end') {
            if (item.id === element.id) {
              if (dragState.type === 'start') {
                const freshEl = canvasElements.find(e => e.id === item.id);
                const proposedStartTime = item.originalValues.startTime + deltaTime;
                const cropDelta = Math.round(proposedStartTime * FPS) - Math.round(item.originalValues.startTime * FPS);
                const proposedCroppedInPoint = (freshEl?.timeline?.croppedInPoint || 0) + cropDelta;

                let adjustedStartTime = proposedStartTime;
                if (proposedCroppedInPoint < 0) {
                  const minDeltaFrames = -(freshEl?.timeline?.croppedInPoint || 0);
                  adjustedStartTime = (Math.round(item.originalValues.startTime * FPS) + minDeltaFrames) / FPS;
                } else {
                  adjustedStartTime = Math.min(proposedStartTime, item.originalValues.startTime + item.originalValues.duration - (1 / FPS));
                }

                // Apply snapping for start resize
                const snapped = applySnap(adjustedStartTime, (item.originalValues.startTime + item.originalValues.duration) - adjustedStartTime, draggedIds, 'start');
                newStartTime = snapped.startTime;
                newDuration = snapped.duration;
                isSnapped = snapped.snapped;

                return { ...item, currentValues: { startTime: newStartTime, duration: newDuration }, isSnapped };
              } else if (dragState.type === 'end') {
                newDuration = Math.max(1 / FPS, item.originalValues.duration + deltaTime);
                if (item.originalValues.startTime + newDuration > duration) {
                  newDuration = duration - item.originalValues.startTime;
                }

                // Apply snapping for end resize
                const snapped = applySnap(item.originalValues.startTime, newDuration, draggedIds, 'end');
                newDuration = snapped.duration;
                isSnapped = snapped.snapped;

                return { ...item, currentValues: { startTime: item.originalValues.startTime, duration: newDuration }, isSnapped };
              }
            }
          } else {
            // Move operation - apply snapping
            const snapped = applySnap(newStartTime, newDuration, draggedIds, 'move');
            newStartTime = snapped.startTime;
            isSnapped = snapped.snapped;
          }

          return { ...item, currentValues: { startTime: newStartTime, duration: newDuration }, isSnapped };
        });

        stateUpdate.multiDragData = newMultiDragData;
      } else {
        const { originalValues } = dragState;
        let newStartTime = originalValues.startTime;
        let newDuration = originalValues.duration;
        const draggedIds = [element.id];
        let isSnapped = false;

        switch (dragState.type) {
          case 'start': {
            const proposedStartTime = originalValues.startTime + deltaTime;
            const cropDelta = Math.round(proposedStartTime * FPS) - Math.round(originalValues.startTime * FPS);
            const proposedCroppedInPoint = (currentElement.timeline.croppedInPoint || 0) + cropDelta;

            if (proposedCroppedInPoint < 0) {
              const minDeltaFrames = -(currentElement.timeline?.croppedInPoint || 0);
              newStartTime = (Math.round(originalValues.startTime * FPS) + minDeltaFrames) / FPS;
            } else {
              newStartTime = Math.min(proposedStartTime, originalValues.startTime + originalValues.duration - (1 / FPS));
            }

            // Apply snapping
            const snapped = applySnap(newStartTime, (originalValues.startTime + originalValues.duration) - newStartTime, draggedIds, 'start');
            newStartTime = snapped.startTime;
            newDuration = snapped.duration;
            isSnapped = snapped.snapped;
            break;
          }
          case 'end':
            newDuration = Math.max(1 / FPS, originalValues.duration + deltaTime);
            if (originalValues.startTime + newDuration > duration) {
              newDuration = duration - originalValues.startTime;
            }

            // Apply snapping
            const snappedEnd = applySnap(originalValues.startTime, newDuration, draggedIds, 'end');
            newDuration = snappedEnd.duration;
            isSnapped = snappedEnd.snapped;
            break;
          case 'move':
            newStartTime = Math.max(0, Math.min(originalValues.startTime + deltaTime, duration - originalValues.duration));

            // Apply snapping
            const snappedMove = applySnap(newStartTime, newDuration, draggedIds, 'move');
            newStartTime = snappedMove.startTime;
            isSnapped = snappedMove.snapped;
            break;
          default: break;
        }

        stateUpdate.currentValues = { startTime: newStartTime, duration: newDuration };
        stateUpdate.isSnapped = isSnapped;
      }
    }

    // Vertical calculations (only if mode is vertical or not yet locked)
    if (!dragState.lockedMode || currentMode === 'vertical') {
      stateUpdate.newVisualIndex = newIndex;
      stateUpdate.deltaY = deltaY;
    }

    // Update state with calculated changes
    setDragState(prev => ({
      ...prev,
      ...stateUpdate
    }));
  }, [dragState, setDragState, duration, element.id, canvasElements, TRACK_HEIGHT, currentElement, FPS, applySnap]);

  const endDrag = useCallback(() => {
    if (!dragState || !dragState.hasMoved) {
      setDragState(null);
      return;
    }

    // Apply vertical changes if there was vertical movement and mode was vertical
    if (dragState.lockedMode === 'vertical' && dragState.deltaY !== 0 && dragState.newVisualIndex !== dragState.originalIndex) {
      if (dragState.isMultiDrag) {
        const draggedIds = dragState.multiDragData.map(item => item.id);
        reorderElement(draggedIds, dragState.newVisualIndex);
      } else {
        reorderElement(dragState.elementId, dragState.newVisualIndex);
      }
    }

    // Apply horizontal changes if there was horizontal movement and mode was horizontal
    if (dragState.lockedMode === 'horizontal' || !dragState.lockedMode) {
      const hasHorizontalChange = dragState.isMultiDrag
        ? dragState.multiDragData.some(item =>
          item.currentValues.startTime !== item.originalValues.startTime ||
          item.currentValues.duration !== item.originalValues.duration
        )
        : dragState.currentValues && (
          dragState.currentValues.startTime !== dragState.originalValues.startTime ||
          dragState.currentValues.duration !== dragState.originalValues.duration
        );

      if (hasHorizontalChange) {
        if (dragState.isMultiDrag) {
          const updates = dragState.multiDragData.map(item => {
            const newInPoint = Math.round(item.currentValues.startTime * FPS);
            const newOutPoint = newInPoint + Math.round(item.currentValues.duration * FPS);
            const timelineUpdates = { inPoint: newInPoint, outPoint: newOutPoint };

            if (dragState.type === 'start' && item.id === element.id) {
              const freshEl = canvasElements.find(e => e.id === item.id);
              const cropDelta = newInPoint - Math.round(item.originalValues.startTime * FPS);
              timelineUpdates.croppedInPoint = (freshEl?.timeline?.croppedInPoint || 0) + cropDelta;
            }

            return { id: item.id, timelineUpdates };
          });
          updateMultipleElementTimelines(updates);
        } else {
          const { currentValues, originalValues, type } = dragState;
          const newInPoint = Math.round(currentValues.startTime * FPS);
          const newOutPoint = newInPoint + Math.round(currentValues.duration * FPS);
          const timelineUpdates = { inPoint: newInPoint, outPoint: newOutPoint };
          if (type === 'start') {
            const cropDelta = newInPoint - Math.round(originalValues.startTime * FPS);
            timelineUpdates.croppedInPoint = (element.timeline.croppedInPoint || 0) + cropDelta;
          }

          updateElementTimeline(element.id, timelineUpdates);
        }
      }
    }

    globalJustFinishedDragging = true;
    if (globalDragTimeout) clearTimeout(globalDragTimeout);
    globalDragTimeout = setTimeout(() => { globalJustFinishedDragging = false; }, 50);

    setDragState(null);
  }, [dragState, setDragState, reorderElement, updateElementTimeline, updateMultipleElementTimelines, element.id, element.timeline?.croppedInPoint, canvasElements, FPS]);

  React.useEffect(() => {
    if (!dragState || dragState.elementId !== element.id) return;

    let cursor = 'auto';
    if (dragState.hasMoved || dragState.mode) {
      // Use locked mode for cursor if available
      const effectiveMode = dragState.lockedMode || dragState.mode;

      if (effectiveMode === 'vertical') cursor = 'grabbing';
      else if (effectiveMode === 'horizontal') {
        cursor = dragState.type === 'move' ? 'grabbing' : 'ew-resize';
      }
    }

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', endDrag);
    document.body.style.cursor = cursor;

    return () => {
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', endDrag);
      document.body.style.cursor = '';
    };
  }, [dragState, handleDrag, endDrag, element.id]);

  const { duration: elementDuration } = getCurrentTimeData();
  const opacity = (isBeingVerticallyDragged) ? 0.5 : 1;

  // Check if this is a placeholder layer (failed to load)
  const isPlaceholder = currentElement.pixiSprite?.isPlaceholder || false;
  const loadError = currentElement.pixiSprite?.loadError || '';
  const currentType = (currentElement.type || currentElement.componentData?.type || '').toLowerCase();
  const aiMediaType = (currentElement.componentData?.aiMediaType || '').toLowerCase();
  const isLikelyAudioTrack = currentType === 'audio' || (currentType === 'ai' && aiMediaType === 'audio');

  // Compute track pixel width first so audio frame count can scale with zoom level.
  const safeTimelineWidth = Number.isFinite(timelineWidth) && timelineWidth > 0 ? timelineWidth : 0;
  const trackPixelWidth = Math.max(
    1,
    (Math.max(currentTimeData.duration, 0) / Math.max(duration, 0.001)) * safeTimelineWidth
  );

  // For audio: snap frame count to 80px buckets to avoid regenerating on every zoom step.
  // For video/image: keep duration-based count.
  const AUDIO_TILE_PX = 22;
  const quantizedTrackWidthPx = Math.max(80, Math.round(trackPixelWidth / 80) * 80);
  const spriteFrameCount = isLikelyAudioTrack
    ? Math.max(6, Math.min(16, Math.ceil(quantizedTrackWidthPx / (AUDIO_TILE_PX * 3))))
    : Math.max(6, Math.min(18, Math.round(Math.max(timeData.duration, 0.5) * 2.2)));

  const { spriteFrames, isLoading: isSpriteLoading, mediaType } = useTimelineTrackSprites({
    element: currentElement,
    clipDurationSec: timeData.duration,
    frameCount: spriteFrameCount,
    fps: FPS,
    enabled: !isPlaceholder,
  });
  const isAudioTrack = mediaType === 'audio';
  const hasSpritePreview = spriteFrames.length > 0;
  const showCenteredLabel = !isAudioTrack && !isSpriteLoading && !hasSpritePreview;
  const showTopLabel = !showCenteredLabel;
  const showSpriteArea = !isPlaceholder && (isAudioTrack || isSpriteLoading || hasSpritePreview);
  const spriteTileWidthPx = isAudioTrack ? 22 : 26;
  const spriteAreaClassName = isAudioTrack
    ? 'absolute inset-x-0 top-[1px] bottom-[1px] pointer-events-none'
    : 'absolute inset-x-0 top-4 bottom-0 pointer-events-none';
  const spriteTileCount = hasSpritePreview
    ? Math.max(
      spriteFrames.length,
      Math.min(360, Math.max(8, Math.ceil(trackPixelWidth / spriteTileWidthPx) + 2))
    )
    : 0;
  const tiledSpriteFrames = useMemo(() => {
    if (!hasSpritePreview) return [];
    if (spriteTileCount <= 1 || spriteFrames.length <= 1) {
      return Array.from({ length: spriteTileCount }, () => spriteFrames[0]);
    }

    // Map visible tiles to sprite frames by timeline progress to keep timing aligned.
    return Array.from({ length: spriteTileCount }, (_, tileIndex) => {
      const progress = tileIndex / (spriteTileCount - 1);
      const sourceIndex = Math.min(
        spriteFrames.length - 1,
        Math.round(progress * (spriteFrames.length - 1))
      );
      return spriteFrames[sourceIndex];
    });
  }, [hasSpritePreview, spriteFrames, spriteTileCount]);

  return (
    <div ref={containerRef} className="h-14">
      <div
        className={`absolute h-12 rounded group transition-colors cursor-grab active:cursor-grabbing overflow-hidden ${isPlaceholder
          ? 'bg-ag-black-700 border-2 border-red-500/60 hover:border-red-500' // Placeholder with red border
          : isSelected
            ? 'bg-ag-black-600 border border-ag-green'
            : 'bg-ag-black-800 hover:bg-ag-black-600'
          } ${isBeingDragged && dragState?.mode === 'horizontal' ? 'transition-none' : ''}`}
        style={{
          left: `${leftPosition}%`,
          width: `${Math.max(width, 0)}%`,
          opacity,
          ...(isPlaceholder && {
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(239, 68, 68, 0.1) 10px, rgba(239, 68, 68, 0.1) 20px)',
          }),
        }}
        onClick={handleTrackClick}
        onMouseDown={(e) => { if (!e.target.closest('.drag-handle')) startDrag(e, 'move'); }}
        title={isPlaceholder ? `Failed to load: ${loadError}` : ''}
      >
        {showSpriteArea && (
          <div className={spriteAreaClassName}>
            {tiledSpriteFrames.length > 0 ? (
              <div className="flex h-full w-full overflow-hidden">
                {tiledSpriteFrames.map((frameSrc, frameIndex) => (
                  <div
                    key={`${element.id}-track-sprite-${frameIndex}`}
                    className="h-full flex-none"
                    style={{ width: `${spriteTileWidthPx}px` }}
                  >
                    <img
                      src={frameSrc}
                      alt=""
                      draggable={false}
                      className={`w-full h-full ${isAudioTrack ? 'object-fill' : 'object-cover'} opacity-95`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`h-full w-full ${isSpriteLoading
                  ? 'animate-pulse bg-ag-black-500/30'
                  : 'bg-gradient-to-r from-ag-black-900/35 via-ag-black-700/20 to-ag-black-900/35'
                  }`}
              />
            )}
          </div>
        )}

        {showTopLabel && (
          <div className={`absolute inset-x-0 top-0 h-4 px-2 pointer-events-none flex items-center justify-center ${isPlaceholder ? 'text-red-300 bg-red-900/10' : 'text-gray-200 bg-ag-black-900/25'
            }`}>
            <span className="truncate text-[10px] leading-none">
              {isPlaceholder && '[!] '}
              {element.name || 'Element'} ({elementDuration.toFixed(1)}s)
              {isPlaceholder && ' - Failed to load'}
            </span>
          </div>
        )}

        {showCenteredLabel && (
          <div className="absolute inset-0 px-2 pointer-events-none flex items-center justify-center">
            <span className={`truncate text-[10px] leading-none px-1.5 py-[2px] rounded ${isPlaceholder ? 'text-red-100 bg-red-900/50' : 'text-gray-200 bg-ag-black-900/35'
              }`}>
              {isPlaceholder && '[!] '}
              {element.name || 'Element'} ({elementDuration.toFixed(1)}s)
              {isPlaceholder && ' - Failed to load'}
            </span>
          </div>
        )}

        {/* Resize Handles (Start/End) - visible on hover or when selected */}
        {isSelected && !isMultiSelected && (
          <>
            {/* Left resize handle */}
            <div
              className={`drag-handle absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-20 opacity-0 group-hover:opacity-100 transition-opacity ${isPlaceholder ? 'bg-red-500' : 'bg-ag-green'
                }`}
              style={{ opacity: isSelected ? 1 : undefined }}
              onMouseDown={(e) => startDrag(e, 'start')}
            />
            {/* Right resize handle */}
            <div
              className={`drag-handle absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize z-20 opacity-0 group-hover:opacity-100 transition-opacity ${isPlaceholder ? 'bg-red-500' : 'bg-ag-green'
                }`}
              style={{ opacity: isSelected ? 1 : undefined }}
              onMouseDown={(e) => startDrag(e, 'end')}
            />
          </>
        )}

      </div>
    </div>
  );
};

export default TimelineTrack;
